'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { setLocalStorageItem } from '@/lib/storage/web-storage';

import type { AdPlacementId } from './placements';
import { resolveAdSlot } from './resolve-ad-slot';
import { useAdSettings } from './use-ad-settings';

type AdContainerProps = {
  placement: AdPlacementId;
  isPaidUser?: boolean;
  showWhyLink?: boolean;
  className?: string;
};

declare global {
  interface Window {
    adsbygoogle?: Array<Record<string, unknown>>;
  }
}

/**
 * Renders a live AdSense unit, a localhost/dev placeholder, or nothing.
 * Decision is entirely in resolveAdSlot; this component only paints the result.
 */
export function AdContainer({
  placement,
  isPaidUser = false,
  showWhyLink = false,
  className,
}: AdContainerProps) {
  const { flags, loaded } = useAdSettings();
  const [mounted, setMounted] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(1024);
  const [hostname, setHostname] = useState('');
  const [isAdLoaded, setIsAdLoaded] = useState(false);
  const uniqueId = useId().replace(/:/g, '');
  const pushedRef = useRef(false);

  const checkViewport = useCallback(() => {
    setViewportWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    setMounted(true);
    setHostname(window.location.hostname);
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [checkViewport]);

  const resolved =
    mounted && loaded
      ? resolveAdSlot({
          placement,
          flags,
          isPaidUser,
          viewportWidth,
          hostname,
        })
      : { kind: 'none' as const };

  useEffect(() => {
    if (resolved.kind !== 'live' || pushedRef.current) {
      return;
    }
    const pushAd = () => {
      try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
        pushedRef.current = true;
        setIsAdLoaded(true);
      } catch {
        /* AdSense blocked or unavailable */
      }
    };
    const timer = window.setTimeout(pushAd, 100);
    return () => window.clearTimeout(timer);
  }, [resolved.kind, resolved.kind === 'live' ? resolved.slotId : '']);

  const onWhyLinkClick = useCallback(() => {
    if (typeof window !== 'undefined') {
      setLocalStorageItem('ad_awareness_shown', '1');
    }
    toast.info('Why are you seeing ads?', {
      description:
        "Running advanced AI infrastructure isn't cheap. To provide this builder for free, these ads help us cover a fraction of our server bills. Upgrade for a distraction-free workspace—we never use predatory auto-subscriptions.",
      duration: 10000,
    });
  }, []);

  if (resolved.kind === 'none') {
    return null;
  }

  const wrapperClass = ['ad-container-wrapper', className].filter(Boolean).join(' ');

  if (resolved.kind === 'placeholder') {
    return (
      <div className={wrapperClass} data-ad-placement={placement} data-ad-mode="placeholder">
        <div
          id={`ad-${uniqueId}`}
          className="ad-container ad-placeholder"
          style={{
            width: '100%',
            minHeight: 90,
            border: '1px dashed #f97316',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(249, 115, 22, 0.06)',
            color: '#9a3412',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: 1,
            padding: 12,
            boxSizing: 'border-box',
          }}
        >
          Ad placeholder · {placement}
        </div>
        {showWhyLink ? (
          <div className="why-link-container">
            <a
              className="why-link"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                onWhyLinkClick();
              }}
            >
              Why am I seeing this?
            </a>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={wrapperClass} data-ad-placement={placement} data-ad-mode="live">
      <div
        id={`ad-${uniqueId}`}
        className={`ad-container${isAdLoaded ? ' ad-loaded' : ''}`}
        style={{ width: '100%', minHeight: 90 }}
      >
        <span
          style={{
            display: 'block',
            fontSize: '0.75rem',
            color: '#aaa',
            textTransform: 'uppercase',
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          Advertisement
        </span>
        <ins
          className="adsbygoogle"
          data-ad-client={resolved.clientId}
          data-ad-slot={resolved.slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
          style={{ display: 'block', width: '100%' }}
        />
      </div>
      {showWhyLink ? (
        <div className="why-link-container">
          <a
            className="why-link"
            href="#"
            onClick={(event) => {
              event.preventDefault();
              onWhyLinkClick();
            }}
          >
            Why am I seeing this?
          </a>
        </div>
      ) : null}
    </div>
  );
}
